/**
 * Demographics code-to-label mappings.
 * Must match the codes used in the CPCFA import and in backend annual reporting
 * (_src/libs/annualReporting/index.js: ethnicityOfPrincipalOwners, genderOfPrincipalOwners,
 * sexualOrientationOfPrincipalOwners, veteranStatusOfPrincipalOwners, middleEasternOrNorthAfricanAncestryOfPrincipalOwners).
 */

/** Ethnicity: H / NH / P from import (annualReporting ETHNICITY_LABELS). */
export const ETHNICITY_LABELS: Record<string, string> = {
  H: 'Hispanic or Latino(a)',
  NH: 'Not Hispanic or Latino(a)',
  P: 'Prefer not to respond',
};

/** Middle Eastern or North African ancestry: M / NM / P from import (annualReporting ANCESTRY_LABELS). */
export const MIDDLE_EASTERN_OR_NORTH_AFRICAN_LABELS: Record<string, string> = {
  M: 'Middle Eastern or North African',
  NM: 'Not Middle Eastern or North African',
  P: 'Prefer not to respond',
};

/** Gender: F / M / N / PS from import (annualReporting GENDER_LABELS). */
export const GENDER_LABELS: Record<string, string> = {
  F: 'Female',
  M: 'Male',
  N: 'Nonbinary',
  PS: 'Prefer to self-describe',
};

/** Sexual orientation: GL / B / S / SE / P from import (annualReporting ORIENTATION_LABELS). */
export const SEXUAL_ORIENTATION_LABELS: Record<string, string> = {
  GL: 'Gay or Lesbian',
  B: 'Bisexual',
  S: 'Straight, that is, not gay, lesbian, or bisexual',
  SE: 'Something Else',
  P: 'Prefer not to respond',
};

/** Veteran status: Y / N / D from import - normalizeVeteranYND (annualReporting veteranStatusOfPrincipalOwners). */
export const VETERAN_STATUS_LABELS: Record<string, string> = {
  Y: 'Yes',
  N: 'No',
  D: 'Prefer not to respond',
};

/** Race value codes to labels (for when race is stored as code string(s) instead of boolean keys). */
export const RACE_LABELS: Record<string, string> = {
  AMERICAN_INDIAN_OR_ALASKA_NATIVE: 'American Indian or Alaska Native',
  ASIAN_INDIAN: 'Asian Indian',
  ASIAN_CHINESE: 'Asian Chinese',
  ASIAN_FILIPINO: 'Asian Filipino',
  ASIAN_JAPANESE: 'Asian Japanese',
  ASIAN_KOREAN: 'Asian Korean',
  ASIAN_VIETNAMESE: 'Asian Vietnamese',
  ASIAN_OTHER: 'Asian Other',
  BLACK_OR_AFRICAN: 'Black or African',
  BLACK_OR_AFRICAN_AMERICAN: 'Black or African American',
  GUAMANIAN_OR_CHAMORRO: 'Guamanian or Chamorro',
  NATIVE_HAWAIIAN: 'Native Hawaiian',
  SAMOAN: 'Samoan',
  PACIFIC_ISLANDER_OTHER: 'Pacific Islander Other',
  WHITE: 'White',
  PREFER_NOT_TO_RESPOND: 'Prefer not to respond',
  BUSINESS_DID_NOT_RESPOND: 'Business did not respond',
};

/**
 * Resolve a demographic code to its display label, or fallback to formatted string.
 * Codes are normalized to uppercase for lookup (import uses Y/N/D, H/NH/P, F/M/N/PS, etc.).
 */
export function getDemographicsLabel(
  value: string | null | undefined,
  codeMap: Record<string, string>,
  fallbackFormatter: (v: string) => string
): string {
  if (value == null || value === '') return '';
  const normalized = String(value).trim().toUpperCase();
  if (codeMap[normalized]) return codeMap[normalized];
  return fallbackFormatter(String(value));
}
